import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { errorMessage } from "../api/client";
import { usePlayer, usePlayerNotes, type PlayerLookup } from "../api/queries";
import type { Player, Session } from "../api/models";
import { carded, theoNumber } from "../in-play/money";
import { formatDuration } from "../in-play/quick-filters";
import { age, mediumDate, timeUntil } from "../players/profile-format";
import {
  entryIdentity,
  entryNudges,
  keepUndismissed,
  mergeEntryToasts,
  propertyInitials,
  reinvestmentText,
  startTimeText,
  tripDate,
  tripTimeFrame,
  TRIP_FILTERS,
  TRIP_SORTS,
  type TimeFrame,
  type TripFilter,
  type TripSort,
} from "../players/profile-logic";
import {
  evaluateGreetRules,
  fetchPlayerEvents,
  fetchProfileNotifications,
  entryKey,
  markProfileNotificationsViewed,
  useActiveSession,
  useActivities,
  usePreferences,
  useReinvestment,
  useTrips,
  type PlayerEvent,
  type Trip,
} from "../players/profile-api";
import { ActivitiesSidebar, GreetsSidebar } from "../players/sidebars/ActivityGreetSidebars";
import { CreateTaskSidebar, EventsSidebar, OverrideSidebar } from "../players/sidebars/EventTaskOverrideSidebars";
import { NotesPrefsSidebar } from "../players/sidebars/NotesPrefsSidebar";
import { ContactSidebar, TasksSidebar } from "../players/sidebars/TaskContactSidebars";
import { Breadcrumb } from "../ui/Breadcrumb";
import { PButton } from "../ui/PButton";
import { PConfirmDialog } from "../ui/PConfirmDialog";
import { PDialog } from "../ui/PDialog";
import { PDropdown } from "../ui/PDropdown";
import { PagedDataView, type DataViewLayout } from "../ui/PagedDataView";
import { PrimeGlyph } from "../ui/PrimeGlyph";
import { SortByPopup } from "../ui/SortByPopup";
import { useToasts } from "../ui/toast";

// PORT OF `players/_pages/player-profile` AND ITS `player-profile-card`, CLASS FOR CLASS: THE
// IN-PLAY LINE, THE PROFILE CARD WITH ITS REINVESTMENT BAND, ACTION BUTTONS AND ACTIVITY TILES,
// THE FOUR TOTALS TILES, AND THE PAGED TRIPS LIST - PLUS EVERY SIDEBAR THE CARD OPENS AND THE
// PROFILE-ENTRY TOASTS.

const ENTRY_TOAST_KEY = "profile-entry";

// SERVES BOTH `/in-play/$playerId` AND `/players/$playerId`. THE PARAM IS A CARD ID.
export function PlayerProfilePage() {
  const { playerId = "" } = useParams({ strict: false });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const expectedCmsPlayerId = useRouterState({ select: (s) => s.location.state.expectedCmsPlayerId });
  const fromInPlay = pathname.startsWith("/in-play/");
  const lookup: PlayerLookup = expectedCmsPlayerId ? { by: "cms", cmsPlayerId: expectedCmsPlayerId } : { by: "card", cardId: playerId };
  const player = usePlayer(lookup);
  const { show: toast } = useToasts();

  // ANGULAR'S TWO FAILED-LOAD TOASTS; THE PAGE ITSELF STAYS EMPTY
  useEffect(() => {
    if (!player.isError) return;
    const wrong = player.error.message.includes("different player");
    toast(
      "error",
      wrong ? "Wrong Player" : "Player Not Loaded",
      wrong
        ? "This link resolved to a different player, so the profile was not opened. Please find the player from the Players page."
        : "The player could not be loaded, so the profile was not opened. Please find the player from the Players page.",
    );
  }, [player.isError, player.errorUpdatedAt, player.error, toast]);

  return (
    <div className="parent">
      {/* KEYED BY PLAYER SO NOTHING (PAGING, A HALF-TYPED NOTE, AN OPEN SIDEBAR) CARRIES OVER */}
      <ProfileBody
        key={`${playerId}|${expectedCmsPlayerId ?? ""}`}
        routeCardId={playerId}
        hasExpectation={!!expectedCmsPlayerId}
        fromInPlay={fromInPlay}
        player={player.data}
        loading={player.isPending}
      />
    </div>
  );
}

function ProfileBody(props: { routeCardId: string; hasExpectation: boolean; fromInPlay: boolean; player: Player | undefined; loading: boolean }) {
  const { routeCardId, hasExpectation, fromInPlay, player, loading } = props;
  const navigate = useNavigate();

  // TRIPS ARE FETCHED BY CARD ID. WITH AN EXPECTATION IN PLAY THEY WAIT FOR THE VERIFIED
  // PLAYER'S OWN CARD (ISSUE #78) AND NEVER FALL BACK TO THE ROUTE PARAM.
  const tripsCardId = hasExpectation ? (player?.cardId ?? null) : routeCardId;

  const [filter, setFilter] = useState<TripFilter>("months-3");
  const [timeFrame, setTimeFrame] = useState<TimeFrame>(() => tripTimeFrame("months-3", null)!);
  const [sort, setSort] = useState<TripSort>("endTime");
  const [desc, setDesc] = useState(true);
  const [page, setPage] = useState(1);
  const [layout, setLayout] = useState<DataViewLayout>("grid");
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null);

  const trips = useTrips(tripsCardId, page, sort, desc ? "desc" : "asc", timeFrame);
  const { show: toast } = useToasts();
  useEffect(() => {
    if (trips.isError) toast("error", "Trips Not Loaded", errorMessage(trips.error));
  }, [trips.isError, trips.errorUpdatedAt, trips.error, toast]);

  const totals = trips.data;
  const paged = totals?.pagedTrips;
  const filterLabel = TRIP_FILTERS.find((f) => f.value === filter)?.name ?? "";
  const sortLabel = TRIP_SORTS.find((s) => s.value === sort)?.name ?? "Day";

  return (
    <>
      <div className="child">
        {/* ANGULAR'S TRAIL ENDS ON THE ROUTE PARAM (THE CARD ID), NOT THE PLAYER'S NAME */}
        <Breadcrumb items={[fromInPlay ? { label: "In-Play", to: "/in-play" } : { label: "Players", to: "/players" }, { label: routeCardId }]} />

        {loading && (
          <div className="flex align-items-center justify-content-center" style={{ minHeight: 200 }}>
            <CircularProgress />
          </div>
        )}

        {player && (
          <div className="grid p-2">
            <div className="col-12 md:col-6 lg:col-6 md:flex-order-1">
              <ProfileCard player={player} timeFrame={timeFrame} totals={totals} />
            </div>
            <div className="col-12 md:col-3 md:flex-order-0 hidden md:block">
              <div className="grid grid-nogutter h-full">
                <div className="col-12 p-0 mb-2">
                  <ActualTile title="Total Actual" cents={totals?.totalActual} />
                </div>
                <div className="col-12 p-0 mt-2">
                  <TheoTile title="Total Theo" cents={totals?.totalTheo} />
                </div>
              </div>
            </div>
            <div className="col-12 md:col-3 md:flex-order-2 hidden md:block">
              <div className="grid grid-nogutter h-full">
                <div className="col-12 p-0 mb-2">
                  <ActualTile title="Average Daily Actual" cents={totals?.averageDailyActual} />
                </div>
                <div className="col-12 p-0 mt-2">
                  <TheoTile title="Average Daily Theo" cents={totals?.averageDailyTheo} />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-3 mb-2 p-1">
          <div className="grid">
            <div className="col-4 md:col-4 hidden md:block flex align-items-center">
              <div className="bg-primary border-round-md p-2 w-8rem text-center">Trips: {totals?.tripCount}</div>
            </div>
            <div className="col-4 md:col-4">
              <div className="flex justify-content-center">
                <Tooltip title="Filter By" placement="bottom">
                  <span>
                    <PButton icon="filter" label={filterLabel} onClick={() => setFilterOpen(true)} />
                  </span>
                </Tooltip>
              </div>
            </div>
            <div className="col-4 md:col-4">
              <div className="p-inputgroup flex justify-content-end">
                <Tooltip title="Sort Direction" placement="bottom">
                  <PButton
                    icon={desc ? "sort-amount-up-alt" : "sort-amount-down-alt"}
                    ariaLabel="Sort Direction"
                    onClick={() => {
                      setDesc(!desc);
                      setPage(1);
                    }}
                  />
                </Tooltip>
                <Tooltip title="Sort By" placement="bottom">
                  <PButton label={sortLabel} onClick={(e) => setSortAnchor(e.currentTarget)} />
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="child-grow card p-0 shadow-1 overflow-x-hidden overflow-y-auto">
        <PagedDataView
          rows={paged?.rows ?? []}
          page={paged?.page ?? page}
          totalPages={Math.max(1, paged?.totalPages ?? 1)}
          totalRows={paged?.totalRows ?? 0}
          onPage={setPage}
          layout={layout}
          onLayout={setLayout}
          emptyMessage={trips.isPending && tripsCardId ? "" : "No player trips available"}
          rowKey={(t) => t.id}
          render={(t, l) => (
            <TripCard
              trip={t}
              layout={l}
              onSessions={() =>
                void navigate({
                  to: fromInPlay ? "/in-play/$playerId/$tripId" : "/players/$playerId/$tripId",
                  params: { playerId: t.playerId || t.player_id || routeCardId, tripId: String(t.id) },
                  state: { trip: t },
                })
              }
            />
          )}
        />
      </div>

      <TripFilterDialog
        open={filterOpen}
        value={filter}
        onClose={() => setFilterOpen(false)}
        onConfirm={(f, custom) => {
          setFilter(f);
          // A CUSTOM RANGE WITHOUT BOTH DATES KEEPS THE FRAME IT HAD, AS IN ANGULAR
          const tf = tripTimeFrame(f, custom);
          if (tf) setTimeFrame(tf);
          setPage(1);
          setFilterOpen(false);
        }}
      />
      <SortByPopup
        anchorEl={sortAnchor}
        onClose={() => setSortAnchor(null)}
        value={sort}
        options={TRIP_SORTS.map((s) => ({ label: s.name, value: s.value }))}
        onChange={(v) => {
          setSort(v);
          setPage(1);
        }}
      />
      {player && <ProfileEntryToasts player={player} />}
    </>
  );
}

// TOTALS TILES --------------------------------------------------------------------------

// `total-daily-actual-pill` IN A `p-card`: THE TILE GOES GREEN OR RED BY SIGN, THE AMOUNT IS
// THE BIG `chart-font-size` WITH AN UP / DOWN ARROW
function ActualTile({ title, cents }: { title: string; cents: number | undefined }) {
  const v = cents ?? 0;
  const tone = v > 0 ? " bg-green-500-card" : v < 0 ? " bg-red-500-card" : "";
  const shown = carded(cents);
  return (
    <StatCard title={title} className={tone}>
      <div className="relative flex flex-row align-items-center justify-content-center font-bold">
        {/* THE ARROW IS A LARGE WATERMARK CENTERED BEHIND THE AMOUNT (ANGULAR DRAWS IT INLINE BEFORE IT), A
            DARKER SHADE OF THE TILE: BLACK AT LOW OPACITY, NOT THE WHITE TEXT COLOR. A `div`, NOT A `span`: `.chart-font-size span` FORCES 2.5REM WITH `!important` */}
        {shown?.arrow && (
          <div
            className="material-symbols-rounded absolute"
            aria-hidden="true"
            style={{ fontSize: "11rem", lineHeight: 1, color: "#000000", opacity: 0.18, top: "50%", left: "50%", transform: "translate(-50%, -50%)", pointerEvents: "none" }}
          >
            {shown.tone === "up" ? "arrow_upward" : "arrow_downward"}
          </div>
        )}
        {/* ZERO SHOWS "$0"; A LOSS THAT ROUNDS TO ZERO SHOWS "-$" (ANGULAR'S PILL, VERBATIM) */}
        <div className="relative">{v > 0 ? shown?.text : v < 0 ? (shown?.text ?? "-$") : "$0"}</div>
      </div>
    </StatCard>
  );
}

// `total-daily-theo` (NO LABEL, BOLD) IN A `bg-primary-700-card`
function TheoTile({ title, cents }: { title: string; cents: number | undefined }) {
  return (
    <StatCard title={title} className=" bg-primary-700-card">
      {/* NO `ml-2` ON THE "$" (ANGULAR KEEPS IT FROM THE HIDDEN LABEL), SO THE AMOUNT IS TRULY CENTERED */}
      <div className="font-bold">
        <span>$</span>
        {theoNumber(cents)}
      </div>
    </StatCard>
  );
}

// THE AMOUNT IS CENTERED BOTH WAYS IN THE WHOLE TILE (ANGULAR SITS IT JUST UNDER THE TITLE). THE TITLE
// IS TAKEN OUT OF THE FLOW AT THE SPOT IT WOULD OCCUPY (`padding: inherit` REPEATS THE BODY'S PADDING),
// SO IT DOES NOT PUSH THE AMOUNT DOWN
function StatCard({ title, className, children }: { title: string; className: string; children: ReactNode }) {
  return (
    <div className={`h-full${className}`}>
      <div className="p-card p-component h-full">
        <div className="p-card-body h-full flex flex-column justify-content-center relative">
          <div className="absolute top-0 left-0 right-0" style={{ padding: "inherit" }}>
            <div className="p-card-title">{title}</div>
          </div>
          <div className="p-card-content flex align-items-center">
            <div className="w-full flex align-items-center justify-content-center">
              <div className="chart-font-size w-10rem mt-0">{children}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// `total-daily-actual`: COLORED, WITH A 24PX ARROW; NOTHING AT ALL FOR ZERO
function ActualInline({ cents }: { cents: number | undefined }) {
  const shown = carded(cents);
  if (!shown) return <div />;
  const tone = shown.tone === "up" ? "text-green-600" : "text-red-600";
  return (
    <div className="flex flex-row align-items-center font-bold">
      {shown.arrow && <span className={`material-symbols-rounded ${tone}`}>{shown.tone === "up" ? "arrow_upward" : "arrow_downward"}</span>}
      <div className={tone}>{shown.text}</div>
    </div>
  );
}

function TheoInline({ cents }: { cents: number | undefined }) {
  return (
    <div className="font-bold">
      <span className="ml-2">$</span>
      {theoNumber(cents)}
    </div>
  );
}

// THE PROFILE CARD ----------------------------------------------------------------------

function Pair({ left, right }: { left: [string, ReactNode]; right: [string, ReactNode] }) {
  return (
    <div className="w-full flex align-items-start justify-content-between">
      <div>
        <div className="font-bold">{left[0]}</div>
        {left[1]}
      </div>
      <div className="flex flex-column align-items-end justify-content-end">
        <div className="font-bold">{right[0]}</div>
        {right[1]}
      </div>
    </div>
  );
}

type Sidebar = "activities" | "greets" | "tasks" | "notes" | "events" | "create-task" | "override" | "contact" | null;

function ProfileCard({ player, timeFrame, totals }: { player: Player; timeFrame: TimeFrame; totals: { totalActual?: number; totalTheo?: number; averageDailyActual?: number; averageDailyTheo?: number } | undefined }) {
  const { show: toast } = useToasts();
  const navigate = useNavigate();
  const active = useActiveSession(player.cmsPlayerId || undefined);
  const reinvestment = useReinvestment(player.id, timeFrame);
  const activities = useActivities(player, 1);
  const notes = usePlayerNotes(player.cmsPlayerId || undefined, 1);
  const prefs = usePreferences(player.patronId);
  const [sidebar, setSidebar] = useState<Sidebar>(null);
  const [events, setEvents] = useState<PlayerEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [offer, setOffer] = useState<string | null>(null);
  const [contactPrefill, setContactPrefill] = useState("");
  // STARTS FROM THE PLAYER'S COUNT; THE GREETS LIST'S TOTAL REPLACES IT ONCE LOADED
  const [greetCount, setGreetCount] = useState<number | undefined>(player.greetCount);

  // `onNotePrefCountChanged`: NOTES AND PREFERENCES ON ONE TILE
  const notePrefCount = notes.data || prefs.data ? (notes.data?.totalRows ?? 0) + (player.patronId ? (prefs.data?.length ?? 0) : 0) : undefined;
  const taskCount = player.taskCount;

  const loadEvents = async (open: boolean) => {
    setLoadingEvents(true);
    try {
      setEvents(await fetchPlayerEvents(player.id));
      if (open) setSidebar("events");
    } catch {
      toast("error", "Get Player Events", "Failed to load player events");
    } finally {
      setLoadingEvents(false);
    }
  };

  const session: Session | null | undefined = active.data;
  const unavailable = active.isError && !session;
  const close = () => setSidebar(null);

  return (
    <div className="card shadow-1 mb-0 h-full">
      {session && (
        <div className="in-play-indicator flex align-items-center flex-wrap gap-2 pl-2 mb-1">
          <span className="in-play-dot" aria-hidden="true" />
          <span className="font-bold uppercase">In Play</span>
          {session.siteName && (
            <span className="font-bold" title={session.siteName}>
              {propertyInitials(session.siteName)}
            </span>
          )}
          {session.description && (
            <span className="in-play-detail">
              <span className="mr-1" style={{ display: "inline-flex" }}>
                <PrimeGlyph name="desktop" size="0.75rem" />
              </span>
              {session.description}
            </span>
          )}
          {session.location && (
            <span className="in-play-detail">
              <span className="mr-1" style={{ display: "inline-flex" }}>
                <PrimeGlyph name="map-marker" size="0.75rem" />
              </span>
              {session.location}
            </span>
          )}
        </div>
      )}
      {unavailable && (
        <div className="in-play-unavailable flex align-items-center gap-2 pl-2 mb-1">
          <PrimeGlyph name="exclamation-circle" size="0.75rem" />
          <span>In-play status unavailable</span>
        </div>
      )}

      <div className="flex justify-content-between align-items-start mb-3 vertical-align-middle">
        <div className="text-lg font-bold pl-2">{(player.name ?? "").toUpperCase()}</div>
        <div className="flex flex-column align-items-end justify-content-end pr-2">
          <div className="font-bold">Card Level:</div>
          {player.playerCardLevel?.description}
        </div>
      </div>
      <div className="p-2">
        <Pair left={["Host:", <div>{player.host ? player.host.hostName : "n/a"}</div>]} right={["Enrolled Since:", <div>{mediumDate(player.enrollDate)}</div>]} />
        <div className="mt-3 w-full flex align-items-start justify-content-between">
          <div>
            <div className="font-bold">Birthdate:</div>
            <div>
              {mediumDate(player.birthdate)} ({timeUntil(player.birthdate)})
            </div>
            <div className="uppercase text-sm font-bold">{age(player.birthdate)} years old</div>
          </div>
          <div className="flex flex-column align-items-end justify-content-end">
            <div className="font-bold">Card ID:</div>
            <div>{(player.cardId ?? "").toUpperCase()}</div>
          </div>
        </div>
      </div>
      <div className="p-2 hidden md:block">
        <Pair left={["Mobile:", player.mobile]} right={["Email:", player.email]} />
      </div>
      <div className="p-2">
        <Pair
          left={["Last Visit:", <div>{player.lastVisit ? mediumDate(player.lastVisit) : "n/a"}</div>]}
          right={["Last Contacted:", <div>{player.lastContact ? mediumDate(player.lastContact) : "n/a"}</div>]}
        />
      </div>
      {/* THE TOTALS REPEAT HERE ON PHONES, WHERE THE TILES ARE HIDDEN */}
      <div className="p-2 block md:hidden">
        <Pair left={["Total Actual:", <ActualInline cents={totals?.totalActual} />]} right={["Total Theo:", <TheoInline cents={totals?.totalTheo} />]} />
      </div>
      <div className="p-2 block md:hidden">
        <Pair left={["Average Daily Actual:", <ActualInline cents={totals?.averageDailyActual} />]} right={["Average Daily Theo:", <TheoInline cents={totals?.averageDailyTheo} />]} />
      </div>

      <div className="p-2 mt-2 player-action-band">
        <div>
          <div className="font-bold">Player Reinvestment</div>
          <div className="mt-1">
            {reinvestment.isPending ? (
              <span className="pillars-skeleton" style={{ display: "block", width: "4rem", height: "1rem", borderRadius: 4 }} />
            ) : (
              reinvestmentText(reinvestment.data)
            )}
          </div>
        </div>
        <div className="player-actions-wrapper">
          <div className="player-action-item">
            <PButton
              label="Contact"
              icon="plus"
              rounded
              className="w-full"
              sx={{ fontWeight: 700 }}
              // `goToPlayerCommunications`: THE PLAYER COMMUNICATIONS PAGE, OPENED ON THIS PLAYER'S CARD
              onClick={() => player.cardId && void navigate({ to: "/player-communications", search: { card: player.cardId } })}
            />
          </div>
          <div className="player-action-item">
            <PButton label="Events" icon="calendar" rounded className="w-full" loading={loadingEvents} sx={{ fontWeight: 700 }} onClick={() => void loadEvents(true)} />
          </div>
          <div className="player-action-item">
            <PButton label="Create Task" icon="check-square" rounded className="w-full" sx={{ fontWeight: 700 }} onClick={() => setSidebar("create-task")} />
          </div>
          <div className="player-action-item">
            <PButton label="Request Override" icon="user-plus" severity="warning" rounded className="w-full" sx={{ fontWeight: 700 }} onClick={() => setSidebar("override")} />
          </div>
        </div>
      </div>

      <div className="grid p-2 mt-2">
        <div className="col-12 md:col-3">
          <ActivityCard label="All Activities" count={activities.data?.totalRows} onClick={() => setSidebar("activities")} />
        </div>
        <div className="col-12 md:col-3">
          <ActivityCard label="Greets" count={greetCount} onClick={() => setSidebar("greets")} />
        </div>
        <div className="col-12 md:col-3">
          {/* ANGULAR OPENS THE TASKS SIDEBAR ONLY WHEN THE COUNT IS NOT EXACTLY ZERO */}
          <ActivityCard label="Tasks" count={taskCount} onClick={() => taskCount !== 0 && setSidebar("tasks")} />
        </div>
        <div className="col-12 md:col-3">
          <ActivityCard label="Notes" count={notePrefCount} onClick={() => setSidebar("notes")} />
        </div>
      </div>

      <ActivitiesSidebar player={player} open={sidebar === "activities"} onClose={close} />
      <GreetsSidebar player={player} open={sidebar === "greets"} onClose={close} count={greetCount} onCount={setGreetCount} />
      <NotesPrefsSidebar player={player} open={sidebar === "notes"} onClose={close} />
      <TasksSidebar player={player} open={sidebar === "tasks"} onClose={close} onOfferContact={setOffer} />
      <ContactSidebar player={player} open={sidebar === "contact"} onClose={close} prefill={contactPrefill} />
      <EventsSidebar player={player} open={sidebar === "events"} events={events} onClose={close} onReload={() => void loadEvents(false)} onOfferContact={setOffer} />
      <CreateTaskSidebar player={player} open={sidebar === "create-task"} onClose={close} />
      <OverrideSidebar player={player} open={sidebar === "override"} onClose={close} />
      <PConfirmDialog
        open={offer !== null}
        header="Create Contact"
        message="Would you like to create a contact from this?"
        acceptLabel="Yes"
        rejectLabel="No"
        onAccept={() => {
          setContactPrefill(offer ?? "");
          setOffer(null);
          setSidebar("contact");
        }}
        onReject={() => setOffer(null)}
      />
    </div>
  );
}

// `activity-card`: THE COUNT (`text-2xl font-bold text-primary`, BLANK UNTIL KNOWN) OVER THE
// `text-sm text-600` LABEL, IN A 75PX CARD
function ActivityCard({ label, count, onClick }: { label: string; count?: number; onClick: () => void }) {
  return (
    <button type="button" className="card pillars-activity-card" onClick={onClick}>
      <div className="flex flex-column align-items-center justify-content-center">
        <div className="text-2xl font-bold text-primary">{count === undefined || count === null ? "" : Number(count).toLocaleString("en-US")}</div>
        <div className="text-sm text-600 text-center">{label}</div>
      </div>
    </button>
  );
}

// TRIPS ---------------------------------------------------------------------------------

// `player-trip-template`: DAY AND A ROUNDED "SESSIONS" BUTTON WITH THE SESSION COUNT, THE
// START TIME AND DURATION, AND THE DAY'S ACTUAL AND THEO
function TripCard({ trip: t, layout, onSessions }: { trip: Trip; layout: DataViewLayout; onSessions: () => void }) {
  const start = (
    <div className="flex flex-row align-items-center">
      <div className="mr-2">
        <span className="material-symbols-outlined">schedule</span>
      </div>
      <div>{startTimeText(t.startTime)}</div>
    </div>
  );
  const duration = (
    <div className="flex flex-row align-items-center">
      <span className="material-symbols-rounded">avg_pace</span>
      <div className="ml-2">{formatDuration(t.duration)}</div>
    </div>
  );
  const head = (
    <div className="flex flex-column justify-content-start w-20rem">
      <div className="pl-1 font-bold">
        <div className="flex-row">
          <div>{tripDate(t.startTime)}</div>
        </div>
      </div>
      <div className="mt-1">
        <PButton label="Sessions" rounded badge={t.sessionCount ?? 0} className="w-8rem" hostElement onClick={onSessions} />
      </div>
    </div>
  );
  const money = (
    <div className="flex flex-column justify-content-end align-items-end w-15rem">
      <ActualInline cents={t.totalDailyActual} />
      <div className="font-bold">
        <span>Theo:</span>
        <span className="ml-2">$</span>
        {theoNumber(t.totalDailyTheo)}
      </div>
    </div>
  );
  if (layout === "list") {
    return (
      <div className="pl-1 pr-1">
        <div className="flex flex-row align-items-center justify-content-between">
          {head}
          <div className="flex flex-column justify-content-start w-15rem">
            <div>{start}</div>
            <div>{duration}</div>
          </div>
          {money}
        </div>
      </div>
    );
  }
  return (
    <div className="p-2 border-1 surface-border surface-card border-round flex flex-column">
      <div>
        <div className="flex flex-row align-items-center justify-content-between">
          {head}
          {money}
        </div>
      </div>
      <div className="mt-1 flex align-items-center justify-content-between">
        <div>{start}</div>
        <div>{duration}</div>
      </div>
    </div>
  );
}

// THE HEADLESS "FILTER BY" CONFIRM: A PRIMARY CIRCLE WITH THE FILTER ICON, THE TIME FRAMES, A
// CUSTOM RANGE AS TWO DATES (THE PILOT'S STAND-IN FOR PRIMENG'S RANGE CALENDAR), CANCEL /
// CONFIRM. UNLIKE ANGULAR, CANCEL LEAVES EVERYTHING AS IT WAS AND FETCHES NOTHING.
function TripFilterDialog({ open, value, onClose, onConfirm }: { open: boolean; value: TripFilter; onClose: () => void; onConfirm: (f: TripFilter, custom: { from: string; to: string } | null) => void }) {
  const [pick, setPick] = useState<TripFilter>(value);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setPick(value);
  }
  return (
    <PDialog open={open} onClose={onClose} ariaLabel="Filter By">
      <div className="flex flex-column align-items-center p-5 surface-overlay border-round">
        <div className="border-circle bg-primary inline-flex justify-content-center align-items-center h-3rem w-3rem">
          <PrimeGlyph name="filter" size="1.5rem" />
        </div>
        <div className="flex flex-column w-full">
          <label className="font-bold">Filter By:</label>
          <PDropdown className="mt-1 w-full" value={pick} placeholder="Select Filter By" options={TRIP_FILTERS.map((f) => ({ label: f.name, value: f.value }))} onChange={setPick} />
          {pick === "custom" && (
            <div className="mt-2 flex gap-2">
              <input type="date" className="p-inputtext w-full" aria-label="From" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
              <input type="date" className="p-inputtext w-full" aria-label="To" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </div>
          )}
        </div>
        <div className="flex align-items-center gap-2 mt-4">
          <PButton label="Cancel" variant="outlined" className="w-8rem" sx={{ color: "var(--gray-500)" }} onClick={onClose} />
          <PButton label="Confirm" className="w-8rem" onClick={() => onConfirm(pick, pick === "custom" ? { from, to } : null)} />
        </div>
      </div>
    </PDialog>
  );
}

// PROFILE-ENTRY TOASTS ------------------------------------------------------------------

// THE STICKY STACK SHOWN ON OPENING A PROFILE: QUEUED ITEMS AND GREET-RULE MATCHES RANKED
// TOGETHER (#108), THEN THE BIRTHDAY / MISSING-CONTACT NUDGES. REBUILT AS EACH SOURCE LANDS,
// MINUS WHATEVER THE HOST HAS ALREADY CLOSED ON THIS VISIT (#113); ONE-TIME ITEMS ARE MARKED
// VIEWED; THE WHOLE STACK IS RETRACTED ON LEAVING THE PROFILE. THE PLAYER'S ACTIONABLE
// PREFERENCES ALSO ANNOUNCE THEMSELVES, ONCE, AS STICKY INFO TOASTS.
function ProfileEntryToasts({ player }: { player: Player }) {
  const { show, clear } = useToasts();
  const cms = player.cmsPlayerId || "";
  const oneShot = { staleTime: Infinity, gcTime: 0, refetchOnWindowFocus: false, retry: false } as const;
  const queued = useQuery({ queryKey: entryKey("profile-notifications", cms), queryFn: ({ signal }) => fetchProfileNotifications(cms, signal), enabled: !!cms, ...oneShot });
  const matches = useQuery({ queryKey: entryKey("greet-rule-matches", cms), queryFn: ({ signal }) => evaluateGreetRules(cms, signal), enabled: !!cms, ...oneShot });
  const prefs = usePreferences(player.patronId);
  const [dismissed, setDismissed] = useState<ReadonlyMap<string, number>>(new Map());

  // MARK THIS LOAD'S ONE-TIME ITEMS VIEWED, ONCE
  const marked = useRef<unknown>(null);
  useEffect(() => {
    if (!queued.data || marked.current === queued.data) return;
    marked.current = queued.data;
    const ids = queued.data.filter((i) => i.oneTimeView).map((i) => i.id);
    if (ids.length) void markProfileNotificationsViewed(cms, ids);
  }, [queued.data, cms]);

  const who = player.firstName || player.name || "This player";
  const stack = useMemo(
    () => keepUndismissed([...mergeEntryToasts(queued.data ?? [], matches.data ?? [], who), ...entryNudges(player)], dismissed),
    [queued.data, matches.data, who, player, dismissed],
  );
  const signature = JSON.stringify(stack.map(entryIdentity));

  useEffect(() => {
    clear(ENTRY_TOAST_KEY);
    for (const t of stack) {
      show(t.severity, t.title, t.message, {
        sticky: true,
        key: ENTRY_TOAST_KEY,
        onClose: () => setDismissed((m) => new Map(m).set(entryIdentity(t), (m.get(entryIdentity(t)) ?? 0) + 1)),
      });
    }
    // THE STACK IS REBUILT ONLY WHEN WHAT IT SHOWS CHANGES
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
  useEffect(() => () => clear(ENTRY_TOAST_KEY), [clear]);

  // ACTIONABLE PREFERENCES, BY PRIORITY - ONCE FOR THIS PROFILE VISIT
  const alerted = useRef(false);
  useEffect(() => {
    if (alerted.current || !prefs.data) return;
    alerted.current = true;
    for (const p of prefs.data.filter((x) => x.actionable === true)) {
      show("info", p.patronPreferenceCategoryName ?? "", p.value ?? "", { sticky: true });
    }
  }, [prefs.data, show]);

  return null;
}

